import { defineComponent, h, inject } from 'vue'

export const interfaceRegistryKey = Symbol('packageInterface')
export const logoDestinationKey = Symbol('packageLogoDestination')

/** The selected interface may export logoDestination as a named Vue Router location. */
export function createInterfaceLogoDestination(destination, source) {
  if (destination == null) {
    return null
  }

  if (typeof destination !== 'object' || Array.isArray(destination) || typeof destination.name !== 'string' || !destination.name.trim()) {
    throw new Error(`[Interface] "logoDestination" in ${source} must be a named Vue Router location.`)
  }

  return Object.freeze({ ...destination })
}

export const interfaceNames = Object.freeze([
  'AppLayout',
  'AuthenticatedLayout',
  'GuestLayout',
  'Logo',
  'TopNavigation',
  'SidebarNavigation',
  'AppFooter',
  'LoginHeader',
  'LoginFooter',
  'LoginPage',
  'RegisterPage',
  'ForgotPasswordPage',
  'ResetPasswordPage',
  'TwoFactorChallengePage',
  'VerifyEmailPage',
  'ConfirmPasswordPage',
  'HomePage',
  'UserPage',
  'NotFoundPage',
])

/** Apply the selected package's optional favicon URL, imported through Vite. */
export function applyInterfaceFavicon(favicon, source, document = globalThis.document) {
  if (favicon == null) {
    return
  }

  if (typeof favicon !== 'string' || !favicon.trim()) {
    throw new Error(`[Interface] "favicon" in ${source} must be a non-empty asset URL.`)
  }

  const icon = document.createElement('link')
  icon.rel = 'icon'
  icon.href = favicon

  document.head.querySelectorAll('link[rel~="icon"]').forEach(link => link.remove())
  document.head.appendChild(icon)
}

export function createInterfaceRegistry(modules = {}) {
  const components = {}
  const sources = Object.keys(modules).sort()

  if (sources.length > 1) {
    throw new Error(`[Interface] Only one package may override the global interface. Received: ${sources.join(', ')}.`)
  }

  for (const path of sources) {
    const overrides = modules[path].default

    if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) {
      throw new Error(`[Interface] ${path} must default-export an object of component overrides.`)
    }

    for (const [name, component] of Object.entries(overrides)) {
      if (!interfaceNames.includes(name)) {
        throw new Error(`[Interface] Unknown override "${name}" in ${path}.`)
      }

      if (!component || !['object', 'function'].includes(typeof component) || Array.isArray(component) || typeof component.then === 'function') {
        throw new Error(`[Interface] "${name}" in ${path} must be a Vue component. Use defineAsyncComponent for lazy components.`)
      }

      components[name] = component
    }
  }

  return Object.freeze(components)
}

export function createInterfaceComponent(name, fallback) {
  if (!interfaceNames.includes(name)) {
    throw new Error(`[Interface] Unknown component "${name}".`)
  }

  return defineComponent({
    name: `Interface${name}`,
    inheritAttrs: false,
    setup(_, { attrs, slots }) {
      const registry = inject(interfaceRegistryKey, {})

      return () => {
        const component = registry[name] || fallback

        return component ? h(component, attrs, slots) : slots.default?.()
      }
    },
  })
}
